/**
 * Forge Score Curve chart — SVG-rendered, no external chart lib.
 *
 * Renders the score curve over a sweep of arrow weights with:
 *   - Sweet spot band (ember-tinted vertical region where score is ideal)
 *   - Curve line in the build's color (ember for A, cyan for B)
 *   - Current marker at the user's selected arrow weight
 *   - Hover tooltip showing the score at any weight
 *   - Tier labels on the right axis (Aerospace / Surgical / Sharp / Tuned)
 */

import { useState, useMemo, useRef } from 'react';
import type { CurveResult, CurvePoint } from '../../lib/forgeScore/curve';

interface Props {
  curve: CurveResult;
  color: 'ember' | 'cyan';
  onArrowWeightChange?: (grains: number) => void;
  species: string;
}

const W = 520;
const H = 240;
const PAD_L = 36;
const PAD_R = 14;
const PAD_T = 14;
const PAD_B = 32;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

const TIER_LINES = [
  { y: 90, label: 'Aerospace', color: '#fbbf24' },
  { y: 75, label: 'Surgical',   color: '#a78bfa' },
  { y: 60, label: 'Sharp',      color: '#34d399' },
];

export function ForgeScoreCurveChart({ curve, color, onArrowWeightChange, species }: Props) {
  const [hover, setHover] = useState<CurvePoint | null>(null);
  const [showHowItsCalculated, setShowHowItsCalculated] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const points = curve.points;
  const minWeight = points[0].arrowGrains;
  const maxWeight = points[points.length - 1].arrowGrains;
  const weightSpan = maxWeight - minWeight;

  const x = (g: number) => PAD_L + ((g - minWeight) / weightSpan) * PLOT_W;
  const y = (s: number) => PAD_T + ((100 - s) / 100) * PLOT_H;

  const pathD = useMemo(() => {
    return points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.arrowGrains).toFixed(1)} ${y(p.forgeScore).toFixed(1)}`)
      .join(' ');
  }, [points]);

  const strokeColor = color === 'ember' ? '#f59e0b' : '#06b6d4';
  const sweetColor = color === 'ember' ? 'rgba(245,158,11,0.18)' : 'rgba(6,182,212,0.18)';

  const handlePointer = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    if (svgX < PAD_L || svgX > PAD_L + PLOT_W) {
      setHover(null);
      return;
    }
    const ratio = (svgX - PAD_L) / PLOT_W;
    const guessedWeight = Math.round((minWeight + ratio * weightSpan) / 10) * 10;
    const point = points.find((p) => p.arrowGrains === guessedWeight) ?? null;
    setHover(point);
  };

  const handleClick = () => {
    if (!hover || !onArrowWeightChange) return;
    onArrowWeightChange(hover.arrowGrains);
  };

  const isAlreadyBest = curve.bestPoint.arrowGrains === curve.currentPoint.arrowGrains;
  const grainDelta = curve.bestPoint.arrowGrains - curve.currentPoint.arrowGrains;
  const scoreDelta = curve.bestPoint.forgeScore - curve.currentPoint.forgeScore;
  const driftDelta = curve.bestPoint.driftIn - curve.currentPoint.driftIn;
  const keDelta = (curve.bestPoint.retainedKePct - curve.currentPoint.retainedKePct) * 100;

  return (
    <div className={`curve-chart-shell curve-${color}`}>
      <div className="curve-header">
        <div className="curve-header-text">
          <h4>
            Build Optimizer
            <button
              type="button"
              className="curve-explain-btn"
              onClick={() => setShowHowItsCalculated((v) => !v)}
              aria-expanded={showHowItsCalculated}
              title="How is the 'best' arrow weight calculated?"
            >
              ?
            </button>
          </h4>
          <p>
            Forge Score vs arrow weight for <strong>{species}</strong>. The shaded band
            is the sweet spot. Click anywhere on the curve to set your build to that
            weight.
          </p>
          {curve.weightFloorGrains && curve.weightFloorReason && (
            <p className="curve-floor-note">
              <strong>Minimum: {curve.weightFloorGrains}gr</strong> for this animal —
              builds below the floor are shown but the Forge will not recommend them as
              "best." {curve.weightFloorReason}
            </p>
          )}
        </div>

        <div className="curve-compare">
          <div className="curve-compare-row">
            <span className="curve-compare-tag">Now</span>
            <span className="curve-compare-detail">
              <strong>{curve.currentPoint.arrowGrains}gr</strong>{' '}
              → <strong>{curve.currentPoint.forgeScore}</strong>{' '}
              <em>{curve.currentPoint.tier}</em>
            </span>
          </div>
          <div className="curve-compare-row curve-compare-row-best">
            <span className="curve-compare-tag">★ Best</span>
            <span className="curve-compare-detail">
              <strong>{curve.bestPoint.arrowGrains}gr</strong>{' '}
              → <strong>{curve.bestPoint.forgeScore}</strong>{' '}
              <em>{curve.bestPoint.tier}</em>
            </span>
          </div>

          {!isAlreadyBest && (
            <div className="curve-compare-delta">
              Apply best changes:{' '}
              <span className={grainDelta >= 0 ? 'd-up' : 'd-down'}>
                {grainDelta >= 0 ? '+' : ''}{grainDelta}gr
              </span>
              {' · '}
              <span className={scoreDelta >= 0 ? 'd-up' : 'd-down'}>
                {scoreDelta >= 0 ? '+' : ''}{scoreDelta} score
              </span>
              {' · '}
              <span className={driftDelta <= 0 ? 'd-up' : 'd-down'}>
                {driftDelta >= 0 ? '+' : ''}{driftDelta.toFixed(1)}&quot; drift
              </span>
              {' · '}
              <span className={keDelta >= 0 ? 'd-up' : 'd-down'}>
                {keDelta >= 0 ? '+' : ''}{keDelta.toFixed(0)}% KE retained
              </span>
            </div>
          )}

          {!isAlreadyBest && onArrowWeightChange && (
            <button
              type="button"
              className="curve-apply-best"
              onClick={() => onArrowWeightChange(curve.bestPoint.arrowGrains)}
              title="Set this build's arrow weight to the optimizer's best point"
            >
              Apply best → {curve.bestPoint.arrowGrains}gr
            </button>
          )}
          {isAlreadyBest && (
            <div className="curve-already-best">
              ✓ Your build is already at the best point for this game.
            </div>
          )}

          {curve.sweetSpot && (
            <div className="curve-sweet-range">
              Sweet-spot band: {curve.sweetSpot.startGrains}–{curve.sweetSpot.endGrains}gr
            </div>
          )}
        </div>
      </div>

      {showHowItsCalculated && (
        <div className="curve-explainer">
          <div className="curve-explainer-row">
            <strong>How the &quot;best&quot; arrow weight is calculated</strong>
            <button
              type="button"
              className="curve-explainer-close"
              onClick={() => setShowHowItsCalculated(false)}
              aria-label="Close explainer"
            >
              ×
            </button>
          </div>
          <p>
            The optimizer sweeps arrow weight from <strong>320 to 700 grains</strong> in 10-grain
            steps, holding everything else constant (your bow, your wind, your altitude, your
            tune-quality, your shot scenario). For each weight it computes the Forge Score using
            <strong> these weights for {species}:</strong>
          </p>
          <ul>
            <li>
              <strong>Tune quality ({curve.effectiveWeights.tuneQuality}%)</strong> — driven by your measured (or estimated) impact yaw.
              Dominates everything else if it&apos;s bad. Below 2° = 100%; above 12° = severe penalty.
            </li>
            <li>
              <strong>Momentum ({curve.effectiveWeights.momentum}%)</strong> — mass × velocity at the species&apos; typical range.
              The most-conserved variable across the whole flight; what actually does penetration work on heavy game.
              {curve.bonePenetrationActive && (
                <>
                  {' '}
                  <em>
                    Dangerous-game bone bonus active: single-bevel COC heads get a +20% multiplier;
                    fixed COC +12%; mechanical heads are penalized 20-45% for deflection / blade collapse
                    on heavy bone (Ashby paradigm).
                  </em>
                </>
              )}
            </li>
            <li><strong>Effective FOC ({curve.effectiveWeights.effectiveFocPenetration}%)</strong> — FOC with the per-shot-scenario deflection probability matrix applied. Caps around 15% FOC; above that the marginal benefit gets small.</li>
            <li><strong>Energy retention ({curve.effectiveWeights.energyRetention}%)</strong> — KE at impact divided by KE at the chronograph. Prevents over-rewarding light-fast builds for ranges they can&apos;t carry to.</li>
          </ul>
          <p>
            The weights above are <strong>species-biased</strong>. Base weights are 40/25/20/15;
            dangerous game shifts the score toward momentum at the expense of tune and FOC, and
            long-range light game shifts toward retention. The biases come from each species&apos;
            profile (typical range, mass class, expected shot scenario).
          </p>
          <p>
            The &quot;best&quot; arrow weight for the selected animal is the highest point on that curve.
            The <strong>sweet-spot band</strong> (shaded region) is every weight where the score
            crosses that species&apos; sweet-spot threshold &mdash; the &quot;any of these is fine&quot; zone.
          </p>
          <p className="curve-explainer-caveat">
            Caveat: only arrow <em>weight</em> varies along this curve. Your FOC, vanes, broadhead, bow tune,
            and bow draw weight are all held constant — but they all feed the score directly through the
            four components above. To compare the effect of a different broadhead or vane, change those
            inputs on the build form and re-read the chart. A full multi-axis optimizer that varies FOC,
            point type, and arrow weight simultaneously is on the roadmap.
          </p>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="curve-svg"
        onPointerMove={handlePointer}
        onPointerLeave={() => setHover(null)}
        onClick={handleClick}
      >
        {/* Weight-floor exclusion zone (species minimum, e.g. cape buffalo). Grey
            mask covers the entire range below the floor so the user sees there's
            a region of the curve they can't pick from. */}
        {curve.weightFloorGrains && curve.weightFloorGrains > points[0].arrowGrains && (
          <g>
            <rect
              x={x(points[0].arrowGrains)}
              y={PAD_T}
              width={x(curve.weightFloorGrains) - x(points[0].arrowGrains)}
              height={PLOT_H}
              fill="rgba(64, 64, 64, 0.55)"
            />
            <line
              x1={x(curve.weightFloorGrains)}
              y1={PAD_T}
              x2={x(curve.weightFloorGrains)}
              y2={PAD_T + PLOT_H}
              stroke="#dc2626"
              strokeWidth={2}
              strokeDasharray="5 3"
            />
            <text
              x={x(curve.weightFloorGrains) + 4}
              y={PAD_T + 12}
              fontSize="10"
              fill="#dc2626"
              fontWeight="600"
            >
              ↑ floor: {curve.weightFloorGrains}gr
            </text>
          </g>
        )}

        {/* Sweet spot band */}
        {curve.sweetSpot && (
          <rect
            x={x(curve.sweetSpot.startGrains)}
            y={PAD_T}
            width={x(curve.sweetSpot.endGrains) - x(curve.sweetSpot.startGrains)}
            height={PLOT_H}
            fill={sweetColor}
            stroke={strokeColor}
            strokeOpacity={0.4}
            strokeDasharray="3 3"
          />
        )}

        {/* Tier reference lines */}
        {TIER_LINES.map((tier) => (
          <g key={tier.label}>
            <line
              x1={PAD_L}
              y1={y(tier.y)}
              x2={W - PAD_R}
              y2={y(tier.y)}
              stroke={tier.color}
              strokeOpacity={0.2}
              strokeDasharray="2 4"
            />
            <text
              x={W - PAD_R + 2}
              y={y(tier.y) + 3}
              fontSize="9"
              fill={tier.color}
              opacity={0.7}
            >
              {tier.label}
            </text>
          </g>
        ))}

        {/* X axis labels */}
        {[400, 460, 520, 580, 640].map((g) => (
          <g key={g}>
            <line
              x1={x(g)}
              y1={PAD_T + PLOT_H}
              x2={x(g)}
              y2={PAD_T + PLOT_H + 4}
              stroke="#52525b"
            />
            <text
              x={x(g)}
              y={PAD_T + PLOT_H + 18}
              fontSize="10"
              fill="#a1a1aa"
              textAnchor="middle"
            >
              {g}gr
            </text>
          </g>
        ))}

        {/* The curve */}
        <path d={pathD} stroke={strokeColor} strokeWidth={2.5} fill="none" />

        {/* Current marker */}
        <circle
          cx={x(curve.currentPoint.arrowGrains)}
          cy={y(curve.currentPoint.forgeScore)}
          r={6}
          fill={strokeColor}
          stroke="#0a0a0a"
          strokeWidth={2}
        />
        <text
          x={x(curve.currentPoint.arrowGrains)}
          y={y(curve.currentPoint.forgeScore) - 10}
          fontSize="10"
          fill="#fafafa"
          textAnchor="middle"
          fontWeight="600"
        >
          {curve.currentPoint.forgeScore}
        </text>

        {/* Best marker */}
        {curve.bestPoint.arrowGrains !== curve.currentPoint.arrowGrains && (
          <g>
            <circle
              cx={x(curve.bestPoint.arrowGrains)}
              cy={y(curve.bestPoint.forgeScore)}
              r={4}
              fill="none"
              stroke={strokeColor}
              strokeWidth={1.5}
              strokeOpacity={0.7}
            />
            <text
              x={x(curve.bestPoint.arrowGrains)}
              y={y(curve.bestPoint.forgeScore) - 8}
              fontSize="9"
              fill={strokeColor}
              textAnchor="middle"
              opacity={0.85}
            >
              ★ best
            </text>
          </g>
        )}

        {/* Hover marker */}
        {hover && hover.arrowGrains !== curve.currentPoint.arrowGrains && (
          <g>
            <line
              x1={x(hover.arrowGrains)}
              y1={PAD_T}
              x2={x(hover.arrowGrains)}
              y2={PAD_T + PLOT_H}
              stroke="#a1a1aa"
              strokeWidth={1}
              strokeOpacity={0.4}
            />
            <circle
              cx={x(hover.arrowGrains)}
              cy={y(hover.forgeScore)}
              r={4}
              fill="#fafafa"
              stroke={strokeColor}
              strokeWidth={2}
            />
          </g>
        )}
      </svg>

      {hover && (
        <div className="curve-hover-info">
          <strong>{hover.arrowGrains}gr</strong> → Forge Score{' '}
          <strong>{hover.forgeScore}</strong> · {hover.tier} · drift{' '}
          {hover.driftIn.toFixed(1)}″ · KE retained {(hover.retainedKePct * 100).toFixed(0)}%
        </div>
      )}
    </div>
  );
}
